import React from 'react';
import { Helmet } from 'react-helmet-async';
import { COMPANY_INFO } from '../mock';

const FAQ_ITEMS_NACH_UNFALL = [
  {
    question: 'Muss ich nach jedem Unfall ein Gutachten machen lassen?',
    answer: 'Nein. Bei kleineren Schäden reicht oft ein Kostenvoranschlag. Bei größeren Schäden oder wenn Wertminderung und Nutzungsausfall relevant sind, ist ein Gutachten sinnvoll. Die Entscheidung ist einzelfallabhängig – wir beraten Sie gerne, bieten aber keine Rechtsberatung an.'
  },
  {
    question: 'Wer bezahlt ein Unfallgutachten?',
    answer: 'Bei einem unverschuldeten Unfall trägt in der Regel die gegnerische Haftpflichtversicherung die Gutachterkosten – je nach Einzelfall und Schadenssituation. Diese Information stellt keine rechtsverbindliche Zusage dar.'
  },
  {
    question: 'Was ist ein Bagatellschaden?',
    answer: 'Ein Bagatellschaden ist ein Schaden geringer Höhe, bei dem häufig ein Kostenvoranschlag ausreicht. Die genaue Grenze ist einzelfallabhängig und sollte im konkreten Fall geprüft werden.'
  },
  {
    question: 'Wie schnell sollte ich nach einem Unfall handeln?',
    answer: 'Möglichst zeitnah. Die Schadensmeldung sollte innerhalb weniger Tage erfolgen. Dokumentation und Fotos am besten direkt am Unfallort erstellen.'
  },
  {
    question: 'Muss ich mein Fahrzeug bewegen lassen?',
    answer: 'Nur wenn es die Sicherheit erfordert. Ansonsten ist es oft ratsam, das Fahrzeug erst nach der Dokumentation (Fotos, ggf. Polizei) zu bewegen.'
  },
  {
    question: 'Was, wenn ich unsicher bin, wie ich vorgehen soll?',
    answer: 'Bei Unsicherheiten können Sie sich unverbindlich beraten lassen. Wir helfen Ihnen gerne weiter und klären, welche Schritte in Ihrer konkreten Situation sinnvoll sind.'
  },
  {
    question: 'Kommt ein Gutachter auch zu mir?',
    answer: 'Ja. Die Begutachtung findet direkt vor Ort statt – bundesweit. Sie müssen nicht zu uns fahren.'
  },
  {
    question: 'Was passiert nach der Begutachtung?',
    answer: 'Sie erhalten das fertige Gutachten zur Vorlage bei der Versicherung. Es dient als Grundlage für die Schadenregulierung. Bei Fragen unterstützen wir Sie gerne.'
  },
  {
    question: 'Kann ich selbst einen Gutachter wählen?',
    answer: 'Ja. Als Geschädigter haben Sie das Recht auf freie Gutachterwahl. Sie sind nicht verpflichtet, einen von der Versicherung vorgeschlagenen Gutachter zu beauftragen.'
  },
  {
    question: 'Wie lange dauert ein Gutachten?',
    answer: 'Die Vor-Ort-Begutachtung dauert meist 30–60 Minuten. Das schriftliche Gutachten erhalten Sie in der Regel innerhalb von 2–3 Werktagen.'
  },
  {
    question: 'Was mache ich, wenn die Versicherung das Gutachten ablehnt?',
    answer: 'In solchen Fällen empfiehlt es sich, rechtlichen Rat einzuholen. Wir selbst bieten keine Rechtsberatung an. Jeder Fall ist individuell – wir können Sie jedoch zum weiteren Vorgehen orientieren.'
  },
  {
    question: 'Kann ich trotz Gutachten selbst entscheiden, wo ich reparieren lasse?',
    answer: 'Ja. Die Wahl der Werkstatt liegt grundsätzlich bei Ihnen. Das Gutachten dient lediglich der Schadensbewertung und gibt Ihnen eine objektive Grundlage. Bei Fragen zu Ihren Ansprüchen empfiehlt sich rechtliche Beratung.'
  },
  {
    question: 'Was ist, wenn der Unfallgegner keine Versicherung hat?',
    answer: 'Das ist eine komplexe Situation, die einzelfallabhängig zu bewerten ist. In solchen Fällen sollten Sie zeitnah rechtlichen Rat einholen, um Ihre Ansprüche zu klären und zu sichern. Wir bieten keine Rechtsberatung an.'
  },
  {
    question: 'Muss ich vor der Reparatur warten?',
    answer: 'In vielen Fällen ist es ratsam, vor Reparaturbeginn eine Begutachtung durchführen zu lassen. So können alle Ansprüche korrekt dokumentiert werden.'
  }
];

export const SEOHeadNachDemUnfall = () => {
  const schemaBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Startseite",
        "item": "https://www.dein-gutachten-direkt.de/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Nach dem Unfall",
        "item": "https://www.dein-gutachten-direkt.de/nach-dem-unfall"
      }
    ]
  };

  const schemaFAQ = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_ITEMS_NACH_UNFALL.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>Nach dem Unfall – Was jetzt wichtig ist | Checkliste & Ratgeber</title>
      <meta name="title" content="Nach dem Unfall – Was jetzt wichtig ist | Checkliste & Ratgeber" />
      <meta name="description" content="Strukturierte Schritt-für-Schritt-Übersicht nach einem Verkehrsunfall. Was Sie beachten sollten, häufige Fehler und nächste Schritte. Ruhig & sachlich erklärt. ☎ 0410 18471160" />
      <meta name="keywords" content="nach dem Unfall, Unfallcheckliste, Verkehrsunfall was tun, Schaden dokumentieren, Unfallgutachten, Polizei rufen, Daten austauschen" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.dein-gutachten-direkt.de/nach-dem-unfall" />
      <meta property="og:title" content="Nach dem Unfall – Was jetzt wichtig ist | Checkliste" />
      <meta property="og:description" content="Strukturierte Schritt-für-Schritt-Übersicht nach einem Verkehrsunfall. Ruhig & sachlich erklärt." />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content="https://www.dein-gutachten-direkt.de/nach-dem-unfall" />
      <meta property="twitter:title" content="Nach dem Unfall – Was jetzt wichtig ist" />
      <meta property="twitter:description" content="Strukturierte Schritt-für-Schritt-Übersicht nach einem Verkehrsunfall" />

      {/* Canonical */}
      <link rel="canonical" href="https://www.dein-gutachten-direkt.de/nach-dem-unfall" />

      {/* Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify(schemaBreadcrumb)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(schemaFAQ)}
      </script>
    </Helmet>
  );
};